<template>
  <b-card class="monthly-report-comment">
    <template #header>
      <div class="d-flex flex-wrap justify-content-between align-items-center">
        <h6 class="mb-0">
          {{ $t('__monthlyReport.commentTitle') }}
          <span v-if="mode" class="badge bg-info ms-2">{{ $t(modeLabelKey(mode)) }}</span>
        </h6>
        <div class="small text-muted">
          <span v-if="comment && comment.published_at">
            <span class="badge bg-success me-1">{{ $t('__monthlyReport.statusPublished') }}</span>
            {{ comment.published_by_name || '-' }} {{ comment.published_at }}
          </span>
          <span v-else-if="comment" class="badge bg-warning">{{ $t('__monthlyReport.statusDraft') }}</span>
          <span v-else class="badge bg-secondary">{{ $t('__monthlyReport.statusNone') }}</span>
        </div>
      </div>
    </template>

    <b-form-group
      v-if="!commentMode"
      :label="$t('__monthlyReport.chooseCommentMode')"
      :description="$t('__monthlyReport.chooseCommentModeHint')"
    >
      <select v-model="mode" class="form-select" :disabled="!canUpdate" @change="onModeChange(mode)">
        <option
          v-for="option in modeOptions"
          :key="String(option.value)"
          :value="option.value"
          :disabled="option.disabled"
        >
          {{ option.text }}
        </option>
      </select>
    </b-form-group>

    <template v-if="mode">
      <b-form-group
        v-for="field in textFields"
        :key="field.key"
        :label="$t(field.labelKey)"
        :label-for="`mr-comment-${field.key}`"
      >
        <b-form-textarea
          :id="`mr-comment-${field.key}`"
          v-model="form[field.key]"
          rows="3"
          max-rows="10"
          :maxlength="textMaxLength"
          :readonly="!canUpdate"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group :label="$t('__monthlyReport.trendStatus')" label-for="mr-comment-trend-status">
        <select id="mr-comment-trend-status" v-model="form.trend_status" class="form-select" :disabled="!canUpdate">
          <option v-for="option in trendOptions" :key="String(option.value)" :value="option.value">
            {{ option.text }}
          </option>
        </select>
      </b-form-group>

      <b-form-group :label="$t('__monthlyReport.trendSummary')" label-for="mr-comment-trend-summary">
        <b-form-textarea
          id="mr-comment-trend-summary"
          v-model="form.trend_summary"
          rows="3"
          max-rows="10"
          :maxlength="textMaxLength"
          :readonly="!canUpdate"
        ></b-form-textarea>
      </b-form-group>

      <div class="d-flex flex-wrap justify-content-between align-items-center mt-4">
        <div class="small text-muted mb-2">
          <template v-if="comment && comment.updated_at">
            {{ $t('__monthlyReport.lastUpdated') }}：{{ comment.updated_by_name || '-' }} {{ comment.updated_at }}
          </template>
          <span v-if="isDirty" class="text-warning ms-2">{{ $t('__monthlyReport.unsavedChanges') }}</span>
        </div>
        <div v-if="canUpdate" class="mb-2">
          <button type="button" class="btn btn-outline-primary rounded-pill px-4" :disabled="isBusy" @click="save(true)">
            {{ $t('__save') }}
          </button>
          <button
            v-if="!isPublished"
            type="button"
            class="btn btn-primary rounded-pill px-4 ms-2"
            :disabled="isBusy"
            @click="publish"
          >
            {{ $t('__monthlyReport.publish') }}
          </button>
          <button
            v-else
            type="button"
            class="btn btn-outline-danger rounded-pill px-4 ms-2"
            :disabled="isBusy"
            @click="unpublish"
          >
            {{ $t('__monthlyReport.unpublish') }}
          </button>
        </div>
      </div>
    </template>
  </b-card>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { monthlyReport, isSuccessResponse, responseErrorMessage } from '@/http/api/monthlyReport'
import {
  MODE_ACTIVE,
  MODE_BED,
  CHART_TITLE_KEYS,
  COMMENT_TEXT_MAX_LENGTH,
  commentToForm
} from './format'

export default {
  name: 'MonthlyReportCommentForm',
  props: {
    residentId: { type: Number, required: true },
    month: { type: String, required: true },
    // 後端 comment_mode：無生效版本時取已存評語的版本，皆無才為 null 需由使用者指定
    commentMode: { type: String, default: null },
    comments: { type: Object, default: () => ({ active: null, bed: null }) },
    canUpdate: { type: Boolean, default: false }
  },
  data () {
    return {
      mode: this.commentMode,
      comment: null,
      form: commentToForm(null),
      snapshot: '',
      isBusy: false,
      textMaxLength: COMMENT_TEXT_MAX_LENGTH
    }
  },
  computed: {
    ...mapState(['token']),
    isDirty () {
      return Boolean(this.mode) && JSON.stringify(this.form) !== this.snapshot
    },
    isPublished () {
      return Boolean(this.comment && this.comment.published_at)
    },
    modeOptions () {
      return [
        { value: null, text: this.$t('__monthlyReport.chooseMode'), disabled: true },
        { value: MODE_ACTIVE, text: this.$t('__monthlyReport.modeActive') },
        { value: MODE_BED, text: this.$t('__monthlyReport.modeBed') }
      ]
    },
    trendOptions () {
      return [
        { value: null, text: this.$t('__monthlyReport.trendUnset') },
        { value: 'improved', text: this.$t('__monthlyReport.trendImproved') },
        { value: 'stable', text: this.$t('__monthlyReport.trendStable') },
        { value: 'worse', text: this.$t('__monthlyReport.trendWorse') }
      ]
    },
    textFields () {
      const chartKeys = CHART_TITLE_KEYS[this.mode] || []
      return [
        { key: 'summary', labelKey: '__monthlyReport.summary' },
        { key: 'respiration', labelKey: '__monthlyReport.respirationRecord' },
        { key: 'chart_1', labelKey: chartKeys[0] },
        { key: 'chart_2', labelKey: chartKeys[1] },
        { key: 'chart_3', labelKey: chartKeys[2] }
      ]
    }
  },
  watch: {
    isDirty (value) {
      this.$emit('dirty-change', value)
    }
  },
  created () {
    if (this.mode) this.loadComment(this.comments[this.mode])
  },
  beforeDestroy () {
    this.$emit('dirty-change', false)
  },
  methods: {
    ...mapMutations(['Loading', 'Loaded', 'AlertsInfo', 'Alerted']),

    modeLabelKey (mode) {
      return mode === MODE_BED ? '__monthlyReport.modeBed' : '__monthlyReport.modeActive'
    },

    loadComment (comment) {
      this.comment = comment || null
      this.form = commentToForm(this.comment)
      this.snapshot = JSON.stringify(this.form)
    },

    // 切換版本時，該版本已有評語就載入；沒有則保留目前輸入
    onModeChange (mode) {
      const saved = this.comments[mode]
      if (saved) {
        this.loadComment(saved)
        return
      }
      this.comment = null
      this.snapshot = JSON.stringify(commentToForm(null))
    },

    notify (state, info) {
      this.AlertsInfo({
        state,
        title: this.$t(state === 'success' ? '__success' : '__error'),
        info
      })
      this.Alerted()
    },

    async request (call, successKey, failKey) {
      this.isBusy = true
      this.Loading()
      try {
        const response = await call()
        if (!isSuccessResponse(response)) {
          this.notify('error', responseErrorMessage(response, this.$t(failKey)))
          return false
        }
        this.loadComment(response.data.data)
        this.$emit('changed', { mode: this.mode, comment: this.comment })
        if (successKey) this.notify('success', this.$t(successKey))
        return true
      } catch (error) {
        console.error('月報評語操作失敗:', error)
        this.notify('error', this.$t(failKey))
        return false
      } finally {
        this.isBusy = false
        this.Loaded()
      }
    },

    save (showSuccess) {
      return this.request(
        () => monthlyReport.saveComment(this.residentId, this.month, this.mode, this.form, this.token),
        showSuccess ? '__monthlyReport.saveSuccess' : null,
        '__monthlyReport.saveFailed'
      )
    },

    async publish () {
      if (this.isDirty || !this.comment) {
        const isSaved = await this.save(false)
        if (!isSaved) return
      }
      await this.request(
        () => monthlyReport.publishComment(this.residentId, this.month, this.mode, this.token),
        '__monthlyReport.publishSuccess',
        '__monthlyReport.publishFailed'
      )
    },

    unpublish () {
      return this.request(
        () => monthlyReport.unpublishComment(this.residentId, this.month, this.mode, this.token),
        '__monthlyReport.unpublishSuccess',
        '__monthlyReport.unpublishFailed'
      )
    }
  }
}
</script>
